import React, { useState } from 'react';
import SupplierBadge from './SupplierBadge';
import SupplierLocation from './SupplierLocation';

function SupplierListItemWithMatch({
  name,
  shortcode,
  caption,
  description,
  logo_url,
  tier,
  city,
  state,
  country,
  rating,
  year_established,
  categories,
  in_house_services,
  tags,
  match,
}) {
  const [feedback, setFeedback] = useState(match?.rfq_feedback || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animatingButton, setAnimatingButton] = useState(null);

  const handleFeedback = async (action) => {
    if (!match || isSubmitting) return;
    
    setIsSubmitting(true);
    setAnimatingButton(action);
    
    // Reset animation after animation completes
    setTimeout(() => setAnimatingButton(null), 300);
    
    try {
      const response = await fetch(`/api/v1/matches/${match.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedback(action === 'like' ? 'liked' : 'disliked');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="box">
      <div className="columns is-vcentered">
        <div className="column is-narrow">
          {logo_url && (
            <figure className="image is-128x128" style={{ position: 'relative' }}>
              <img
                src={logo_url}
                alt={name}
                style={{ objectFit: 'contain', maxHeight: '100%' }}
              />
              {match && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '12px',
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFeedback('like');
                    }}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transform: animatingButton === 'like' ? 'scale(1.2)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (animatingButton !== 'like') e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <i 
                      className={`ph${feedback === 'liked' ? '-fill' : ''} ph-thumbs-up`}
                      style={{
                        fontSize: '16px',
                        color: '#4a4a4a',
                        transition: 'transform 0.2s ease',
                        transform: animatingButton === 'like' ? 'scale(1.1)' : 'scale(1)',
                      }}
                    ></i>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFeedback('dislike');
                    }}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transform: animatingButton === 'dislike' ? 'scale(1.2)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (animatingButton !== 'dislike') e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <i 
                      className={`ph${feedback === 'disliked' ? '-fill' : ''} ph-thumbs-down`}
                      style={{
                        fontSize: '16px',
                        color: '#4a4a4a',
                        transition: 'transform 0.2s ease',
                        transform: animatingButton === 'dislike' ? 'scale(1.1)' : 'scale(1)',
                      }}
                    ></i>
                  </div>
                </div>
              )}
            </figure>
          )}
        </div>
        <div className="column">
          <h3 className="title is-5 mb-2">
            <a
              href={`/supplier_profiles/${shortcode}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </a>
          </h3>
          <p className="subtitle is-6 mb-2">{caption}</p>
          {description && (
            <p className="content has-text-grey mb-2">{description}</p>
          )}
          <div className="is-flex is-flex-wrap-wrap is-align-items-center">
            <div className="mr-4 mb-1">
              <SupplierBadge tier={tier} />
            </div>
            <div className="mr-4 mb-1">
              <SupplierLocation city={city} state={state} country={country} />
            </div>
            {rating && (
              <span className="mr-4 mb-1">
                {rating} <i className="ph-fill ph-star reviewStar"></i>
              </span>
            )}
            {year_established && (
              <span className="has-text-grey mb-1">Est. {year_established}</span>
            )}
          </div>
          {(categories?.length > 0 || in_house_services?.length > 0) && (
            <div className="tags mt-2">
              {categories?.map((category) => (
                <span key={category} className="tag is-light">
                  {category}
                </span>
              ))}
              {in_house_services?.map((service) => (
                <span key={service} className="tag is-info is-light">
                  {service}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="column is-narrow">
          <div className="has-text-right">
            <a
              href={`/supplier_profiles/${shortcode}`}
              className="button is-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact supplier
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupplierListItemWithMatch;